import { BackdropModal } from '@ui';
import type { FC, KeyboardEventHandler } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { flushSync } from 'react-dom';
import API from '@api';
import { queryClient } from '@root/src/providers/query';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  SEARCH_QUERIES_QUERY_KEY,
  peopleSearchQueriesQueryOptions,
  productsBySearchBoosterQueryOptions,
  searchExampleQueryOptions
} from '@queries/index';
import debounce from 'lodash/debounce';
import { checkToClearLocalStorage } from '@utils/localStorage';
import { rand } from '@utils/numbers';
import { useHistory } from '@compat/router';
import HeaderSearchMobile from './Mobile';
import HeaderSearchDesktop from './Desktop';
import PageLayout from '@root/src/PageLayout';
import {
  CLOSE_MOBILE_SEARCH_EVENT,
  MOBILE_SEARCH_VISIBILITY_EVENT,
  OPEN_MOBILE_SEARCH_EVENT
} from '@constants/customEvents';
import type {
  GetPeopleSearchQueriesQuery,
  GetProductsBySearchBoosterQuery
} from '@schema/cart';
import { OrderDir, ProductSort } from '@schema/cart';
import { observer } from 'mobx-react';
import useWindowSize from '@customHooks/useWindowSize';
import type { SearchTip } from './index.type';
import usePreviousValue from '@customHooks/usePreviousValue';
import { useStores } from '@root/src/stores';

export interface HeaderSearchSettings {
  isScroll: boolean;
  showCatalogList: boolean;
  searchValue: string;
  searchData: {
    query: string | null;
    products: Product[];
    categories: NonNullable<
      GetProductsBySearchBoosterQuery['getProductsBySearchBooster']['categories']
    >;
  };
  isSearchPage: boolean;
  searchQueries: NonNullable<
    GetPeopleSearchQueriesQuery['getPeopleSearchQueries']
  >;
  isLoading: boolean;
  showSearchResult: boolean;
  itemForExample: string;
  tips: SearchTip[];
  isInputFocused: boolean;
  handleSearchInput: () => void;
  searchInputOnClick: () => void;
  searchInputOnBlur: () => void;
  searchInputOnBlurInput: () => void;
  onClickInputContainer: (e: React.MouseEvent) => void;
  searchInputOnKeyUp: KeyboardEventHandler;
  searchHeader: (e: { target: { value: string }; persist: () => void }) => void;
  onClickForExample: () => void;
  clearInput: () => void;
  onSearchBtnClick: () => void;
  onRemove: (queryId: number) => void;
  onChangeLink: (link: string) => void;
}

interface Product {
  id: string;
  name: string;
  link_url: string;
  image_url: string | null | undefined;
  available: boolean;
  price: string;
  isDigital: boolean;
  rating: number;
  commentNumber: number;
}

const Header: FC = observer(() => {
  const { userStore } = useStores();
  const { isDesktop } = useWindowSize();
  const history = useHistory();
  const [isScroll, setIsScroll] = useState(false);
  const [showCatalogFocus, setShowCatalogFocus] = useState(false);
  const getIsSearchPage = history.location.pathname.includes('/search');

  const getInitialSearchValue = (): string => {
    let searchValue = '';
    if (getIsSearchPage) {
      const params = new URLSearchParams(history.location.search);
      searchValue = params.get('query') || '';
    }
    return searchValue;
  };

  const [searchValue, setSearchValue] = useState(getInitialSearchValue());

  const [searchData, setSearchData] = useState<{
    query: string | null;
    products: Product[];
    categories: NonNullable<
      GetProductsBySearchBoosterQuery['getProductsBySearchBooster']['categories']
    >;
  }>(
    (() => ({
      query: getInitialSearchValue() ?? null,
      products: [],
      categories: []
    }))()
  );
  const [itemForExample, setItemForExample] = useState('черная икра');
  const [isLoading, setIsLoading] = useState(true);
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [showCatalogList, setShowCatalogList] = useState(false);
  const [tips, setTips] = useState<SearchTip[]>([]);

  const { data: searchQueries = [] } = useQuery({
    ...peopleSearchQueriesQueryOptions(),
    select: list => list.slice(0, 5),
    enabled: !userStore.isUserLoading && userStore.isUserLoggedIn
  });

  const { data: itemForExamples = [] } = useQuery(searchExampleQueryOptions());

  const prevSearchValue = usePreviousValue(searchValue);

  const handleSearchInputFire = useCallback(async (): Promise<void> => {
    if (prevSearchValue !== searchValue && searchData.query === searchValue) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    if (!userStore.isUserLoading && userStore.isUserLoggedIn) {
      void queryClient.invalidateQueries({
        queryKey: [SEARCH_QUERIES_QUERY_KEY]
      });
    }

    await queryClient
      .ensureQueryData(
        productsBySearchBoosterQueryOptions({
          userInput: searchValue,
          orderDir: OrderDir.Desc,
          orderBy: ProductSort.Ids,
          isFullSearch: false
        })
      )
      .then(res => {
        const data = res.getProductsBySearchBooster;
        const catalogItems = data?.catalogItems || [];
        setTips(data.tips || []);
        if (Number(catalogItems?.length) > 0) {
          const digitalProductsMap = {};
          catalogItems.forEach(item => {
            digitalProductsMap[item.id] = item.digital === 1;
          });

          const products: Product[] = catalogItems.map(item => ({
            id: item.id,
            name: item.title || '',
            link_url: `/product/${item.id}`,
            image_url: item?.fileItem?.cacheItem?.[0]?.filePath,
            available: !!item.available,
            price: `${item.currentPriceField2}`,
            isDigital: !!item.digital,
            rating: item.rating_comments || 0,
            commentNumber: item.comments_number || 0
          }));
          setSearchData(prev => ({
            ...prev,
            ...data,
            query: searchValue,
            categories: data.categories || [],
            products
          }));
        }

        if (
          /^\d+$/.test(searchValue) &&
          parseInt(searchValue, 10) !== 0 &&
          catalogItems.filter(a => +a.id === parseInt(searchValue, 10))
            .length === 0
        ) {
          const item = catalogItems[0];
          const product: Product = {
            id: item.id,
            name: item.title || '',
            link_url: `/product/${item.id}`,
            image_url: item.fileItem?.cacheItem?.[0]?.filePath,
            available: !!item.available,
            price: `${item.currentPriceField} ${''}`,
            isDigital: item.digital === 1,
            rating: item.rating_comments || 0,
            commentNumber: item.comments_number || 0
          };
          setSearchData(prev => {
            const newSearchData = { ...prev };

            newSearchData.products = newSearchData.products || [];
            newSearchData.query = searchValue;

            newSearchData.products = [
              product,
              ...(newSearchData?.products || [])
            ];

            return newSearchData;
          });
        }

        setSearchData(prev => ({
          ...prev,
          query: searchValue
        }));
        setIsLoading(false);
      })
      .catch(error => {
        console.log(`Error: ${error}`);
        setIsLoading(false);
      });
  }, [
    prevSearchValue,
    searchData.query,
    searchValue,
    userStore.isUserLoading,
    userStore.isUserLoggedIn
  ]);

  const handleSearchInputFireRef = useRef(handleSearchInputFire);
  useEffect(() => {
    handleSearchInputFireRef.current = handleSearchInputFire;
  });

  const openMobileSearchRef = useRef((): void => {});
  openMobileSearchRef.current = (): void => {
    flushSync(() => {
      setShowSearchResult(true);
      if (!history.location.pathname.includes('/search')) {
        setSearchValue('');
      }
    });
    document.getElementById('search-input')?.focus();
    void handleSearchInputFireRef.current();
  };

  useEffect(() => {
    const openSearch = (): void => {
      openMobileSearchRef.current();
    };
    const closeSearch = (): void => {
      setShowSearchResult(false);
      setShowCatalogFocus(false);
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        (active.id === 'search-input' || active.id === 'header-search-input')
      ) {
        active.blur();
      }
    };

    window.addEventListener(OPEN_MOBILE_SEARCH_EVENT, openSearch);
    window.addEventListener(CLOSE_MOBILE_SEARCH_EVENT, closeSearch);
    return () => {
      window.removeEventListener(OPEN_MOBILE_SEARCH_EVENT, openSearch);
      window.removeEventListener(CLOSE_MOBILE_SEARCH_EVENT, closeSearch);
    };
  }, []);

  const handleSearchInputDebounced = useMemo(
    () =>
      debounce(async () => {
        await handleSearchInputFireRef.current();
      }, 500),
    []
  );

  const delayedCallbackDebounced = useMemo(
    () =>
      debounce(async () => {
        await handleSearchInputFireRef.current();
      }, 500),
    []
  );

  useEffect(
    () => () => {
      handleSearchInputDebounced.cancel();
      delayedCallbackDebounced.cancel();
    },
    [handleSearchInputDebounced, delayedCallbackDebounced]
  );

  const updateSearchExample = (): void => {
    const index = rand(0, itemForExamples.length - 1);

    if (typeof itemForExamples[index] === 'undefined') {
      return;
    }
    setItemForExample(itemForExamples[index]);
  };

  useEffect(() => {
    if (itemForExamples.length) {
      setItemForExample(itemForExamples[0]);
    }
  }, [itemForExamples]);

  const handleWindowKey = (e): void => {
    if (
      showCatalogFocus ||
      e.target.tagName?.toLowerCase() === 'input' ||
      e.target.tagName?.toLowerCase() === 'textarea' ||
      !checkKey(e)
    ) {
      return;
    }

    setSearchValue(searchValue);
    document.getElementById('search-input')?.focus();
  };

  const checkKey = (e): boolean => {
    if (typeof e.which === 'undefined') {
      return true;
    } else if (typeof e.which === 'number' && e.which > 0) {
      return (
        !e.ctrlKey && !e.metaKey && !e.altKey && e.which >= 48 && e.which <= 90
      );
    }

    return false;
  };

  const handleScroll = useCallback((): void => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    setIsScroll(scrollTop >= 15);
  }, []);

  const handleWindowKeyRef = useRef(handleWindowKey);
  useEffect(() => {
    handleWindowKeyRef.current = handleWindowKey;
  });

  const stableHandleWindowKey = useMemo(
    () => (e: Event) => {
      handleWindowKeyRef.current(e);
    },
    []
  );

  useEffect(() => {
    const unlisten = history.listen(location => {
      setShowCatalogList(false);
      setShowSearchResult(false);

      const searchParams = new URLSearchParams(location.search);
      const params = Object.fromEntries(searchParams.entries());
      if (params?.query !== searchValue) {
        setSearchValue(params?.query || '');
      }

      updateSearchExample();
    });

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', stableHandleWindowKey);

    checkToClearLocalStorage();
    return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', stableHandleWindowKey);
    },
    [handleScroll, stableHandleWindowKey]
  );

  const onClickInputContainer = (e): void => {
    e.stopPropagation();
    setShowSearchResult(true);
    handleSearchInputFireRef.current();
    window?.dataLayer?.push({ event: 'webit_click_search_box' });
  };

  const searchInputOnBlur = (): void => {
    setShowSearchResult(false);
  };

  const searchInputOnBlurInput = (): void => {
    setShowCatalogFocus(false);
  };

  const focusInputOff = (): void => {
    const focusOff = document.getElementById('search-input');
    setTimeout(() => focusOff?.blur?.(), 50);
  };

  const onSearchBtnClick = (): void => {
    setShowSearchResult(false);
    history.push(`/search?query=${searchValue}`);
    focusInputOff();
  };

  const searchInputOnKeyUp: KeyboardEventHandler = (e): void => {
    const isEnter = e.which === 13;
    const pushUrl = (): void => {
      history.push(`/search?query=${searchValue}`);
    };
    const isSearchPage = getIsSearchPage;
    if (
      isEnter &&
      typeof searchData.products !== 'undefined' &&
      searchData.products.length === 1 &&
      !isSearchPage
    ) {
      // Press Enter and we have only one search result
      history.push(`/product/${searchData.products?.[0]?.id}`);
      focusInputOff();
    } else if (isEnter && !isSearchPage) {
      pushUrl();
      focusInputOff();
    } else if (isEnter) {
      setShowSearchResult(false);
      pushUrl();
      focusInputOff();
    }
  };

  const searchInputOnClick = (): void => {
    openMobileSearchRef.current();
  };

  const clearInput = (): void => {
    setSearchValue('');
    setSearchData(prev => ({
      ...prev,
      query: ''
    }));
    document.getElementById('search-input')?.focus();
  };

  const onClickForExample = (): void => {
    setSearchValue(itemForExample);
    setShowSearchResult(false);
    history.push(`/search?query=${itemForExample}`);
    focusInputOff();
    delayedCallbackDebounced();
  };

  const searchHeader = (e): void => {
    e.persist();
    setSearchValue(e.target.value.replace(/%/g, ''));
    delayedCallbackDebounced();
  };

  const deleteSearchQueryMutation = useMutation({
    mutationFn: (queryId: number) => API.deletePeopleSearchQuery({ queryId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [SEARCH_QUERIES_QUERY_KEY]
      });
    }
  });

  const onRemove = (queryId: number): void => {
    deleteSearchQueryMutation.mutate(queryId);
  };

  const onChangeLink = (link: string): void => {
    const task = { target: { value: link }, persist: () => {} };
    searchHeader(task);
    if (history?.location?.pathname !== '/search') {
      history.push(`/search?query=${link}`);
    }
  };

  const changeModalState = (): void => {
    setShowCatalogFocus(false);
    setShowSearchResult(false);
  };

  // ЭТАП 1 · Шаг 3 — SSR-guard: window.innerWidth в рендере. На сервере window
  // нет → терм = false; initial-рендер всё равно false (второй операнд —
  // showSearchResult||showCatalogFocus — стартово false), гидрация без рассинхрона.
  const showSearchModal =
    !(getIsSearchPage && typeof window !== 'undefined' && window.innerWidth > 1280) &&
    (showSearchResult || showCatalogFocus);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(MOBILE_SEARCH_VISIBILITY_EVENT, {
        detail: showSearchModal
      })
    );
  }, [showSearchModal]);

  const settings: HeaderSearchSettings = {
    isScroll,
    showCatalogList,
    searchValue,
    searchData,
    handleSearchInput: handleSearchInputDebounced,
    isLoading,
    showSearchResult: showSearchModal,
    itemForExample,
    tips,
    isInputFocused: showSearchResult || showCatalogFocus,
    isSearchPage: getIsSearchPage,
    searchQueries,
    searchInputOnClick,
    searchInputOnBlur,
    searchInputOnBlurInput,
    onClickInputContainer,
    searchInputOnKeyUp,
    searchHeader,
    onClickForExample,
    clearInput,
    onSearchBtnClick,
    onRemove,
    onChangeLink
  };

  return (
    <>
      {showSearchModal && isDesktop && (
        <BackdropModal onClose={changeModalState} />
      )}
      <PageLayout
        renderDesktop={HeaderSearchDesktop}
        settingDesktop={settings}
        renderLaptop={HeaderSearchMobile}
        settingLaptop={settings}
        renderMobile={HeaderSearchMobile}
        settingMobile={settings}
      />
    </>
  );
});

export default Header;
